import React, { useMemo } from "react";
import {
  useGlobalFilter,
  useTable,
  usePagination,
  useSortBy
} from 'react-table';
import {
  TiArrowUnsorted,
  TiArrowSortedUp,
  TiArrowSortedDown
} from 'react-icons/ti';
import { GlobalFilter } from "./GlobalFilter";

// helper to mask everything but the first N chars
const maskValue = (raw, visibleChars = 3) => {
  const s = String(raw);
  if (s === 'n/a' || s.length <= visibleChars) return s;
  return s.slice(0, visibleChars) + '•'.repeat(s.length - visibleChars);
};

const Table = (props) => {
  const { accountType = 'Trial', people } = props;

  const handleNextPageClick = () => {
    nextPage();
  };

  const handlePrevPageClick = () => {
    previousPage();
  };

  const peopleData = useMemo(() => [...people], [people]);
  const peopleColumns = useMemo(
    () =>
      people[0]
        ? Object.keys(people[0]).filter(key => key !== 'checked' && key !== 'notes' && key !== 'id').map(key => {
            if (key === 'id') {
              return {
                Header: key,
                accessor: key,
                Cell: e => e.value + 1
              };
            }
            if (key === 'email') {
              return {
                Header: key,
                accessor: key,
                Cell: e =>
                  e.value === 'n/a'
                    ? e.value
                    : <a href={`mailto:${e.value}`}>{e.value}</a>
              };
            }
            if (key === 'profileLink') {
              return {
                Header: key,
                accessor: key,
                Cell: e =>
                  e.value === 'n/a'
                    ? e.value
                    : <a href={e.value} target="_blank" rel="noopener noreferrer">{e.value}</a>
              };
            }
            return { Header: key, accessor: key };
          })
        : [],
    [people]
  );

  const tableInstance = useTable(
    {
      columns: peopleColumns,
      data: peopleData,
      initialState: { pageSize: 20 }
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    getToggleHideAllColumnsProps,
    headerGroups,
    page,
    nextPage,
    canNextPage,
    previousPage,
    canPreviousPage,
    pageOptions,
    setPageSize,
    allColumns,
    preGlobalFilteredRows,
    setGlobalFilter,
    state,
    prepareRow
  } = tableInstance;

  const { pageIndex, pageSize } = state;

  return (
    <>
      {people.length > 1 && (
        <GlobalFilter
          preGlobalFilteredRows={preGlobalFilteredRows}
          setGlobalFilter={setGlobalFilter}
          globalFilter={state.globalFilter}
          peopleData={peopleData}
          allColumns={allColumns}
          latestSearchId={props.latestSearchId}
          getToggleHideAllColumnsProps={getToggleHideAllColumnsProps}
        />
      )}
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map(headerGroup => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map(column => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render('Header')}
                  {column.isSorted
                    ? (column.isSortedDesc
                        ? <TiArrowSortedDown />
                        : <TiArrowSortedUp />)
                    : <TiArrowUnsorted />}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map(row => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map(cell => {
                  const colId = cell.column.id;
                  // mask all non-name columns for Trial users
                  if (accountType === 'Trial' && colId !== 'name') {
                    return (
                      <td {...cell.getCellProps()}>
                        {maskValue(cell.value)}
                      </td>
                    );
                  }
                  // otherwise render normally
                  return (
                    <td {...cell.getCellProps()}>
                      {cell.render('Cell')}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      {people.length > 1 && (
        <div className="pagination-controlls-container">
          <p className="page-count-text">
            Page <strong>{pageIndex + 1} of {pageOptions.length}</strong>
          </p>
          <select
            value={pageSize}
            onChange={e => setPageSize(Number(e.target.value))}
          >
            {[20, 50, 100].map(size => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </select>
          <button onClick={handlePrevPageClick} disabled={!canPreviousPage}>
            previous
          </button>
          <button onClick={handleNextPageClick} disabled={!canNextPage}>
            next
          </button>
        </div>
      )}
    </>
  );
};

export default Table;
